/**
 * Valuation Calculation Utilities
 * Auto-calculation functions for financial data fields
 */

/**
 * Parse a formatted number string (with commas) to a float
 */
export const parseNumber = (value) => {
  if (!value && value !== 0) return 0;
  const cleanValue = value.toString().replace(/,/g, '');
  return parseFloat(cleanValue) || 0;
};

/**
 * Calculate Income Statement auto-calculated fields
 * @param {Object} financialData - Financial data object
 * @param {number} year - Year to calculate for
 * @returns {Object} - Updated financial data for the year
 */
export const calculateIncomeStatement = (financialData, year) => {
  const revenue = parseNumber(financialData.revenue?.[year]);
  const cogs = parseNumber(financialData.cogs?.[year]);
  const operating_expenses = parseNumber(financialData.operating_expenses?.[year]);
  const depreciation = parseNumber(financialData.depreciation?.[year]);
  const interest_expense = parseNumber(financialData.interest_expense?.[year]);
  const tax_expense = parseNumber(financialData.tax_expense?.[year]);

  // Calculate Gross Profit
  const gross_profit = revenue - cogs;

  // Calculate EBITDA
  const ebitda = gross_profit - operating_expenses;

  // Calculate EBIT
  const ebit = ebitda - depreciation;

  // Calculate Net Income (Profit After Tax)
  const profit_after_tax = ebit - interest_expense - tax_expense;

  // Calculate Add-backs Total
  const addBackFields = [
    'owner_salary', 'family_salaries', 'salary_adjustments', 'auto_expenses',
    'donations', 'insurance_premiums', 'inventory_adjustments', 'meals_entertainment',
    'personal_expenses', 'phone_internet', 'non_business_services', 'travel',
    'one_time_charges', 'retirement', 'supplier_rebates', 'other_addbacks'
  ];

  const addbacks_total = addBackFields.reduce((sum, field) => {
    return sum + parseNumber(financialData[field]?.[year]);
  }, 0);

  // Calculate SDE
  const sde = ebitda + addbacks_total;

  return {
    gross_profit: gross_profit.toString(),
    ebitda: ebitda.toString(),
    ebit: ebit.toString(),
    profit_after_tax: profit_after_tax.toString(),
    addbacks_total: addbacks_total.toString(),
    sde: sde.toString()
  };
};

/**
 * Calculate Balance Sheet auto-calculated fields
 * @param {Object} financialData - Financial data object
 * @param {number} year - Year to calculate for
 * @returns {Object} - Updated financial data for the year
 */
export const calculateBalanceSheet = (financialData, year) => {
  const cash = parseNumber(financialData.cash?.[year]);
  const current_assets = parseNumber(financialData.current_assets?.[year]);
  const non_current_assets = parseNumber(financialData.non_current_assets?.[year]);
  const short_term_debt = parseNumber(financialData.short_term_debt?.[year]);
  const long_term_debt = parseNumber(financialData.long_term_debt?.[year]);

  // Calculate Total Assets
  const total_assets = cash + current_assets + non_current_assets;

  // Calculate Total Liabilities
  const total_liabilities = short_term_debt + long_term_debt;

  // Calculate Equity
  const equity = total_assets - total_liabilities;

  return {
    total_assets: total_assets.toString(),
    total_liabilities: total_liabilities.toString(),
    equity: equity.toString()
  };
};

/**
 * Calculate Cash Flow auto-calculated fields
 * @param {Object} financialData - Financial data object
 * @param {number} year - Year to calculate for
 * @param {number} previousYear - Previous year for working capital change calculation
 * @returns {Object} - Updated financial data for the year
 */
export const calculateCashFlow = (financialData, year, previousYear) => {
  const profit_after_tax = parseNumber(financialData.profit_after_tax?.[year]);
  const depreciation = parseNumber(financialData.depreciation?.[year]);
  const capex = parseNumber(financialData.capex?.[year]);

  // Calculate Working Capital Change
  let working_capital_change = 0;
  if (previousYear) {
    const current_assets_current = parseNumber(financialData.current_assets?.[year]);
    const short_term_debt_current = parseNumber(financialData.short_term_debt?.[year]);
    const current_assets_previous = parseNumber(financialData.current_assets?.[previousYear]);
    const short_term_debt_previous = parseNumber(financialData.short_term_debt?.[previousYear]);

    const wc_current = current_assets_current - short_term_debt_current;
    const wc_previous = current_assets_previous - short_term_debt_previous;
    working_capital_change = wc_previous - wc_current; // Negative if WC increased (cash outflow)
  }

  // Calculate Free Cash Flow
  const free_cash_flow = profit_after_tax + depreciation - capex + working_capital_change;

  return {
    working_capital_change: working_capital_change.toString(),
    free_cash_flow: free_cash_flow.toString()
  };
};

/**
 * Apply all auto-calculations for a given year
 * @param {Object} financialData - Financial data object
 * @param {number} year - Year to calculate for
 * @param {Array} allYears - Array of all years for previous year lookup
 * @returns {Object} - Complete updated financial data for the year
 */
export const applyAutoCalculations = (financialData, year, allYears) => {
  const yearIndex = allYears.indexOf(year);
  const previousYear = yearIndex > 0 ? allYears[yearIndex - 1] : null;

  const incomeCalculations = calculateIncomeStatement(financialData, year);
  const balanceCalculations = calculateBalanceSheet(financialData, year);
  const cashFlowCalculations = calculateCashFlow(financialData, year, previousYear);

  return {
    ...incomeCalculations,
    ...balanceCalculations,
    ...cashFlowCalculations
  };
};

/**
 * Apply auto-calculations for all years
 * @param {Object} financialData - Financial data object
 * @param {Array} years - Array of years to calculate for
 * @returns {Object} - Complete updated financial data object
 */
export const applyAllAutoCalculations = (financialData, years) => {
  const updatedData = { ...financialData };

  years.forEach(year => {
    const calculations = applyAutoCalculations(financialData, year, years);

    // Update each calculated field for this year
    Object.keys(calculations).forEach(field => {
      if (!updatedData[field]) {
        updatedData[field] = {};
      }
      updatedData[field][year] = calculations[field];
    });
  });

  return updatedData;
};

/**
 * Calculate financial ratios for a given year
 * @param {Object} financialData - Financial data object
 * @param {number} year - Year to calculate ratios for
 * @returns {Object} - Financial ratios
 */
export const calculateRatios = (financialData, year) => {
  const revenue = parseNumber(financialData.revenue?.[year]);
  const gross_profit = parseNumber(financialData.gross_profit?.[year]);
  const ebitda = parseNumber(financialData.ebitda?.[year]);
  const current_assets = parseNumber(financialData.current_assets?.[year]);
  const cash = parseNumber(financialData.cash?.[year]);
  const short_term_debt = parseNumber(financialData.short_term_debt?.[year]);
  const total_liabilities = parseNumber(financialData.total_liabilities?.[year]);
  const equity = parseNumber(financialData.equity?.[year]);
  const total_assets = parseNumber(financialData.total_assets?.[year]);
  const profit_after_tax = parseNumber(financialData.profit_after_tax?.[year]);

  // Profitability Ratios
  const grossMargin = revenue > 0 ? (gross_profit / revenue) * 100 : 0;
  const ebitdaMargin = revenue > 0 ? (ebitda / revenue) * 100 : 0;

  // Liquidity Ratios
  const currentRatio = short_term_debt > 0 ? current_assets / short_term_debt : 0;
  const cashRatio = short_term_debt > 0 ? cash / short_term_debt : 0;

  // Leverage Ratios
  const debtToEquity = equity > 0 ? total_liabilities / equity : 0;
  const debtToAssets = total_assets > 0 ? (total_liabilities / total_assets) * 100 : 0;

  // Efficiency Ratios
  const assetTurnover = total_assets > 0 ? revenue / total_assets : 0;
  const returnOnAssets = total_assets > 0 ? (profit_after_tax / total_assets) * 100 : 0;
  const returnOnEquity = equity > 0 ? (profit_after_tax / equity) * 100 : 0;

  return {
    grossMargin,
    ebitdaMargin,
    currentRatio,
    cashRatio,
    debtToEquity,
    debtToAssets,
    assetTurnover,
    returnOnAssets,
    returnOnEquity
  };
};
